import { useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'

import { useFormik } from 'formik'
import { debounce } from 'lodash'

import { balanceReportSelector } from '@/state/balance-report/slices/index.slices'
import {
  BalanceReportData,
  BalanceReportFilters,
  BalanceReportLibraryColumnProps,
} from '@/state/balance-report/models'

import { invertedNormalDatePaymentFormat } from '@/constants/date'
import { ApplicationState } from '@/state/root.models'
import { ADMIN_VIEW } from '@/constants/brand'

import { TableHeadCellPropsType } from '@/state/ui/global/models'
import useBalanceReportActions from './use-balance-report-actions.hooks'
import { initialValuesBalanceReport } from './schema'

const useBalanceReportFilters = () => {
  const { getBalanceReport, setBalanceReportFilters } =
    useBalanceReportActions()

  const { filters, pagination } = useSelector(balanceReportSelector)

  const { brand } = useSelector(({ auth }: ApplicationState) => auth)

  const formikBalanceReport = useFormik({
    initialValues: initialValuesBalanceReport,
    onSubmit: () => {},
    enableReinitialize: true,
  })

  const buildFilters = (
    payload: BalanceReportFilters,
    headCells?: TableHeadCellPropsType<BalanceReportLibraryColumnProps>[],
    fileName?: string,
    getFormattedRow?: (
      row: BalanceReportData,
    ) => BalanceReportLibraryColumnProps,
  ) => {
    const filter: BalanceReportFilters & {
      lastPlayedFrom?: string
      lastPlayedTo?: string
    } = {
      page: payload.page || 1,
      limit: payload.limit || 25,
    }

    if (payload.playerId) {
      filter.playerId = payload.playerId
    }

    if (payload.startDate) {
      const newStartDate = new Date(payload.startDate.toString())
      if (!isNaN(newStartDate.getTime())) {
        filter.lastPlayedFrom = format(
          newStartDate,
          invertedNormalDatePaymentFormat,
        )
      }
    }

    if (payload.endDate) {
      const newEndDate = new Date(payload.endDate.toString())
      if (!isNaN(newEndDate.getTime())) {
        filter.lastPlayedTo = format(
          newEndDate,
          invertedNormalDatePaymentFormat,
        )
      }
    }

    if (brand.siteId !== ADMIN_VIEW) {
      filter.brandId = brand.siteId.toString()
    }

    getBalanceReport(
      { ...filter, xlsx: payload.xlsx },
      headCells,
      fileName,
      getFormattedRow,
    )
  }

  // Always call the freshest builder from the debounced wrapper.
  const buildFiltersRef = useRef(buildFilters)
  buildFiltersRef.current = buildFilters

  const debouncedBuildFilters = useMemo(
    () =>
      debounce(
        (payload: BalanceReportFilters) => buildFiltersRef.current(payload),
        500, // eslint-disable-line
      ),
    [],
  )

  const submitSearchFilter = (customFilters?: BalanceReportFilters) => {
    buildFilters(customFilters || { ...filters })
  }

  const handleChangeSearch = (field: string, value: any) => {
    const newFilters = {
      ...filters,
      [field]: value,
      page: 1,
    } as BalanceReportFilters

    setBalanceReportFilters(newFilters)
    formikBalanceReport.setFieldValue(field, value)
    debouncedBuildFilters(newFilters)

    return newFilters
  }

  const handleBulkChangeSearch = (newValues: Partial<BalanceReportFilters>) => {
    const newFilters = {
      ...filters,
      ...newValues,
      page: 1,
    } as BalanceReportFilters

    setBalanceReportFilters(newFilters)
    formikBalanceReport.setValues({
      ...formikBalanceReport.values,
      ...newValues,
    } as any)
    debouncedBuildFilters(newFilters)

    return newFilters
  }

  const handleChangePage = (newPage: number) => {
    const newFilters = { ...filters, page: newPage }
    setBalanceReportFilters(newFilters)
    buildFilters(newFilters)
  }

  const handleChangeLimit = (newLimit: number) => {
    const shouldResetPage =
      pagination.page > Math.ceil(pagination.total / newLimit)
    const newFilters = {
      ...filters,
      limit: newLimit,
      page: shouldResetPage ? 1 : filters.page,
    }
    setBalanceReportFilters(newFilters)
    buildFilters(newFilters)
  }

  const downloadXlsxSearchFilter = (
    filtersPayload: BalanceReportFilters,
    headCells?: TableHeadCellPropsType<BalanceReportLibraryColumnProps>[],
    fileName?: string,
    getFormattedRow?: (
      row: BalanceReportData,
    ) => BalanceReportLibraryColumnProps,
  ) => {
    buildFilters(filtersPayload, headCells, fileName, getFormattedRow)
  }

  return {
    filters,
    formikBalanceReport,
    submitSearchFilter,
    handleChangeSearch,
    handleBulkChangeSearch,
    handleChangePage,
    handleChangeLimit,
    downloadXlsxSearchFilter,
  }
}

export default useBalanceReportFilters
